import { useState } from 'react';
import { useTranslation } from "react-i18next";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDropzone } from "react-dropzone";
import { ModalLayout } from "../../components/Modal/ModalLayout";
import { FormField } from "../../components/Form/FormField";
import { useProducts } from "../../hooks/useProducts";
import { useAreas } from "../../hooks/useAreas";
import { CreateProductDto } from "../../types/product";

interface AddProductModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const AddProductModal = ({ isOpen, onClose }: AddProductModalProps) => {
  const { t } = useTranslation();
  const { createProduct } = useProducts();
  const { areas } = useAreas();
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const validationSchema = Yup.object({
    code: Yup.string().required(t("validation.required")),
    name: Yup.string().required(t("validation.required")),
    description: Yup.string().required(t("validation.required")),
    price: Yup.number()
      .required(t("validation.required"))
      .positive(t("validation.positive")),
    availAreas: Yup.array().of(Yup.string()),
  });

  const convertToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = error => reject(error);
    });
  };

  const formik = useFormik<CreateProductDto & { logoFile?: File }>({
    initialValues: {
      code: "",
      name: "",
      description: "",
      price: 0,
      availAreas: [],
      logoFile: undefined,
    },
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      try {
        let logo;
        if (values.logoFile) {
          const base64String = await convertToBase64(values.logoFile);
          logo = {
            small: base64String,
            medium: base64String,
            large: base64String,
          };
        }

        const productData: CreateProductDto = {
          code: values.code,
          name: values.name,
          description: values.description,
          price: values.price,
          availAreas: values.availAreas,
          ...(logo && { logo }),
        };
        
        await createProduct.mutateAsync(productData);

        resetForm();
        setPreviewUrl(null);
        onClose();
      } catch (error) {
        console.error("Failed to create product:", error);
      }
    },
  });

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg', '.gif']
    },
    maxSize: 10485760, // 10MB
    onDrop: (acceptedFiles) => {
      const file = acceptedFiles[0];
      formik.setFieldValue('logoFile', file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  });

  const handleAreaChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedValue = e.target.value;
    formik.setFieldValue('availAreas', selectedValue ? [selectedValue] : []);
  };

  const handleClose = () => {
    formik.resetForm();
    setPreviewUrl(null);
    onClose();
  };

  return (
    <ModalLayout
      isOpen={isOpen}
      onClose={handleClose}
      title={t("products.modal.addProduct")}
    >
      <form onSubmit={formik.handleSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <FormField
              label={t("products.modal.code")}
              name="code"
              placeholder={t("products.modal.codePlaceholder")}
              formik={formik as unknown as ReturnType<typeof useFormik>}
            />

            <FormField
              label={t("products.modal.name")}
              name="name"
              placeholder={t("products.modal.namePlaceholder")}
              formik={formik as unknown as ReturnType<typeof useFormik>}
            />

            <FormField
              label={t("products.modal.description")}
              name="description"
              type="textarea"
              placeholder={t("products.modal.descriptionPlaceholder")}
              formik={formik as unknown as ReturnType<typeof useFormik>}
            />

            <FormField
              label={t("products.modal.price")}
              name="price"
              type="number"
              placeholder={t("products.modal.pricePlaceholder")}
              formik={formik as unknown as ReturnType<typeof useFormik>}
            />

            <div>
              <label className="block text-sm font-medium mb-2">
                {t("products.modal.availableArea")}
              </label>
              <select
                name="availAreas"
                value={formik.values.availAreas[0] || ''}
                onChange={handleAreaChange}
                onBlur={formik.handleBlur}
                className="select select-bordered w-full focus:outline-none"
              >
                <option value="">{t("products.modal.selectArea")}</option>
                {areas.map((area) => (
                  <option key={area._id} value={area._id}>
                    {area.name}
                  </option>
                ))}
              </select>
              {formik.touched.availAreas && formik.errors.availAreas && (
                <div className="text-error text-sm mt-1">{formik.errors.availAreas}</div>
              )}
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">
                {t("products.modal.image")}
              </label>
              <div
                {...getRootProps()}
                className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer
                  ${isDragActive ? "border-primary bg-primary/5" : "border-gray-300"}`}
              >
                <input {...getInputProps()} />
                {previewUrl ? (
                  <div className="space-y-2">
                    <img 
                      src={previewUrl} 
                      alt="Preview" 
                      className="max-h-32 mx-auto object-contain"
                    />
                    <p className="text-sm text-gray-600">
                      {t("products.modal.dragAndDropNew")}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="text-4xl text-gray-400">↑</div>
                    <p className="text-sm text-gray-600">
                      {isDragActive
                        ? t("products.modal.dropHere")
                        : t("products.modal.dragAndDrop")}
                    </p>
                    <p className="text-xs text-gray-500">
                      {t("products.modal.imageRequirements")}
                    </p>
                  </div>
                )}
              </div>
              {formik.values.logoFile && (
                <div className="mt-2 text-sm text-gray-600">
                  Selected file: {formik.values.logoFile.name}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button type="button" className="btn btn-ghost" onClick={handleClose}>
            {t("common.cancel")}
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={formik.isSubmitting}
          >
            {t("products.modal.save")}
          </button>
        </div>
      </form>
    </ModalLayout>
  );
}; 